import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import CollectionHeader from './components/CollectionHeader';
import ItemsGrid from './components/ItemsGrid';
import ItemForm from './components/ItemForm';
import CSVImport from './components/CSVImport';
import ShareModal from './components/ShareModal';
import { CollectionsService } from '../../services/collectionsService';
import { CollectiblesService } from '../../services/collectiblesService';


const CollectionDetail = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams?.get('id');
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collection, setCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState('');

  useEffect(() => {
    if (!collectionId) {
      setError('Collection ID is required');
      return;
    }
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadCollectionData();
  }, [collectionId, user]);

  useEffect(() => {
    filterAndSortItems();
  }, [items, searchQuery, sortBy, sortOrder, filterBy]);

  const loadCollectionData = async () => {
    try {
      setLoading(true);
      setError('');

      const [collectionResult, itemsResult] = await Promise.all([
        CollectionsService?.getCollectionById(collectionId),
        CollectiblesService?.getCollectionItems(collectionId)
      ]);

      if (collectionResult?.error) {
        throw new Error(typeof collectionResult.error === 'string' ? collectionResult.error : collectionResult.error?.message || 'Failed to load collection');
      }

      if (itemsResult?.error) {
        throw new Error(itemsResult.error?.message || itemsResult.error || 'Failed to load items');
      }

      const collectionData = collectionResult?.data;
      const itemsData = itemsResult?.data || [];

      // Check if user owns this collection
      if (user && collectionData?.user_id !== user?.id) {
        setError('You do not have permission to view this collection');
        return;
      }

      setCollection(collectionData);
      setItems(itemsData);
      setNewCollectionName(collectionData?.name || '');

    } catch (err) {
      setError(err?.message || 'Failed to load collection data');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...items];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(item => 
        item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.brand?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply condition filter
    if (filterBy !== 'all') {
      filtered = filtered?.filter(item => item?.condition === filterBy);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'current_value' || sortBy === 'purchase_price') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else {
        aValue = String(aValue || '')?.toLowerCase();
        bValue = String(bValue || '')?.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredItems(filtered);
  };

  const handleAddItem = async (itemData) => {
    try {
      const collectibleData = {
        ...itemData,
        user_id: user?.id,
        collection_id: collectionId,
        collectible_type: collection?.collectible_type
      };

      const { data: newItem, error } = await CollectiblesService?.createCollectible(collectibleData);

      if (error) {
        throw error;
      }

      setItems(prev => [newItem, ...prev]);
      setShowItemForm(false);
      setEditingItem(null);

    } catch (err) {
      setError(err?.message || 'Failed to add item');
    }
  };

  const handleEditItem = async (itemData) => {
    try {
      const { data: updatedItem, error } = await CollectiblesService?.updateCollectible(
        editingItem?.id, 
        itemData
      );

      if (error) {
        throw error;
      }

      setItems(prev => prev?.map(item => 
        item?.id === editingItem?.id ? updatedItem : item
      ));
      setShowItemForm(false);
      setEditingItem(null);

    } catch (err) {
      setError(err?.message || 'Failed to update item');
    }
  };

  const handleDeleteItems = async () => {
    try {
      await Promise.all(
        selectedItems?.map(itemId => 
          CollectiblesService?.deleteCollectible(itemId)
        )
      );

      setItems(prev => prev?.filter(item => !selectedItems?.includes(item?.id)));
      setSelectedItems([]);
      setShowDeleteModal(false);

    } catch (err) {
      setError(err?.message || 'Failed to delete items');
    }
  };

  const handleRenameCollection = async () => {
    try {
      const { data: updatedCollection, error } = await CollectionsService?.updateCollection(
        collectionId, 
        { name: newCollectionName }
      );

      if (error) {
        throw new Error(typeof error === 'string' ? error : error?.message || 'Failed to rename collection');
      }

      setCollection(updatedCollection);
      setShowRenameModal(false);

    } catch (err) {
      setError(err?.message || 'Failed to rename collection');
    }
  };

  const handleDeleteCollection = async () => {
    try {
      const { error } = await CollectionsService?.deleteCollection(collectionId);

      if (error) {
        throw new Error(typeof error === 'string' ? error : error?.message || 'Failed to delete collection');
      }

      navigate('/collection-dashboard', {
        state: { message: 'Collection deleted successfully' }
      });

    } catch (err) {
      setError(err?.message || 'Failed to delete collection');
    }
  };

  const handleCSVImport = async (importedItems) => {
    try {
      const results = await Promise.all(
        importedItems?.map(itemData => 
          CollectiblesService?.createCollectible({
            ...itemData,
            user_id: user?.id,
            collection_id: collectionId,
            collectible_type: collection?.collectible_type
          })
        )
      );

      const newItems = results?.filter(result => !result?.error)?.map(result => result?.data);

      setItems(prev => [...newItems, ...prev]);
      setShowCSVImport(false);

    } catch (err) {
      setError(err?.message || 'Failed to import items');
    }
  };

  const handleSaveSharing = async (isPublic, showcaseSettings) => {
    try {
      const result = await CollectionsService?.updateSharingSettings(
        collectionId,
        isPublic,
        showcaseSettings
      );

      if (result?.error) {
        throw new Error(result.error);
      }

      setCollection(prev => ({
        ...prev,
        is_public: isPublic,
        showcase_settings: showcaseSettings
      }));

      setShowShareModal(false);
    } catch (err) {
      setError(err?.message || 'Failed to update sharing settings');
    }
  };

  const downloadCSVTemplate = () => {
    const headers = ['name', 'brand', 'model', 'year', 'condition', 'purchase_price', 'current_value', 'description', 'notes'];
    
    const csvContent = [
      headers?.join(','),
      // Example row
      'Example Item,Example Brand,Model X,2020,mint,100,150,Item description,Optional notes'
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `${collection?.name || 'collection'}_template.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex pt-16">
          <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
          
          <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-6 lg:p-8">
              <div className="max-w-md mx-auto text-center">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertTriangle" size={32} className="text-destructive" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Failed to Load Collection
                </h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <div className="flex space-x-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/collection-dashboard')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back to Dashboard
                  </Button>
                  <Button
                    onClick={loadCollectionData}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{collection?.name ? `${collection?.name} | Crazy Rare Club` : 'Collection Detail | Crazy Rare Club'}</title>
        <meta name="description" content={collection?.description || `Explore ${collection?.name || 'this collection'} on Crazy Rare Club — the premier platform for rare collectibles.`} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`https://crazyrareclub.com/collection-detail?id=${collectionId}`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Crazy Rare Club" />
        <meta property="og:title" content={collection?.name ? `${collection?.name} | Crazy Rare Club` : 'Collection Detail | Crazy Rare Club'} />
        <meta property="og:description" content={collection?.description || `Explore ${collection?.name || 'this collection'} on Crazy Rare Club — the premier platform for rare collectibles.`} />
        <meta property="og:url" content={`https://crazyrareclub.com/collection-detail?id=${collectionId}`} />
        <meta property="og:image" content={collection?.cover_image || 'https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png'} />
        <meta property="og:image:alt" content={`${collection?.name || 'Collection'} cover image`} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={collection?.name ? `${collection?.name} | Crazy Rare Club` : 'Collection Detail | Crazy Rare Club'} />
        <meta name="twitter:description" content={collection?.description || `Explore ${collection?.name || 'this collection'} on Crazy Rare Club.`} />
        <meta name="twitter:image" content={collection?.cover_image || 'https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png'} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": collection?.name || "Collection",
          "description": collection?.description || "A curated collection of rare collectibles on Crazy Rare Club.",
          "url": `https://crazyrareclub.com/collection-detail?id=${collectionId}`,
          "image": collection?.cover_image || "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png",
          "numberOfItems": items?.length || 0,
          "publisher": {
            "@type": "Organization",
            "name": "Crazy Rare Club",
            "url": "https://crazyrareclub.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://crazyrareclub.com/assets/images/crc_logo_dia_logo_500-1759554868300.png"
            }
          }
        })}</script>
      </Helmet>
      <Header />
      <div className="flex pt-16">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 lg:p-8">
            {/* Collection Header */}
            <CollectionHeader
              collection={collection}
              itemCount={filteredItems?.length}
              onRename={() => setShowRenameModal(true)}
              onDelete={() => setShowDeleteModal(true)}
              onShare={() => setShowShareModal(true)}
              onBack={() => navigate('/collection-dashboard')}
            />

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between mb-6 space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none sm:w-80">
                  <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10"
                  />
                </div>
                
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e?.target?.value)}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                >
                  <option value="all">All Conditions</option>
                  <option value="mint">Mint</option>
                  <option value="near-mint">Near Mint</option>
                  <option value="excellent">Excellent</option>
                  <option value="very-good">Very Good</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>

                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e?.target?.value?.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                >
                  <option value="created_at-desc">Newest First</option>
                  <option value="created_at-asc">Oldest First</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="current_value-desc">Highest Value</option>
                  <option value="current_value-asc">Lowest Value</option>
                </select>
              </div>

              <div className="flex items-center space-x-3">
                {selectedItems?.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteModal(true)}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete ({selectedItems?.length})
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  onClick={downloadCSVTemplate}
                  iconName="Download"
                  iconPosition="left"
                >
                  CSV Template
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowCSVImport(true)}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Import CSV
                </Button>
                
                <Button
                  onClick={() => setShowItemForm(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Item
                </Button>
              </div>
            </div>

            {/* Items Grid */}
            <ItemsGrid
              items={filteredItems}
              selectedItems={selectedItems}
              onSelectionChange={setSelectedItems}
              onEdit={(item) => {
                setEditingItem(item);
                setShowItemForm(true);
              }}
              onDelete={(item) => {
                setSelectedItems([item?.id]);
                setShowDeleteModal(true);
              }}
            />

            {/* Empty State */}
            {filteredItems?.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Package" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {items?.length === 0 ? 'No Items Yet' : 'No Items Found'}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {items?.length === 0 
                    ? 'Start building your collection by adding your first item.' :'Try adjusting your search or filter criteria.'
                  }
                </p>
                {items?.length === 0 && (
                  <div className="flex justify-center space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowCSVImport(true)}
                      iconName="Upload"
                      iconPosition="left"
                    >
                      Import CSV
                    </Button>
                    <Button
                      onClick={() => setShowItemForm(true)}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add First Item
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {showItemForm && (
        <ItemForm
          item={editingItem}
          collectionType={collection?.collectible_type}
          onSave={editingItem ? handleEditItem : handleAddItem}
          onClose={() => {
            setShowItemForm(false);
            setEditingItem(null);
          }}
        />
      )}
      {showCSVImport && (
        <CSVImport
          collectionType={collection?.collectible_type}
          onImport={handleCSVImport}
          onClose={() => setShowCSVImport(false)}
        />
      )}
      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">
              Rename Collection
            </h3>
            <Input
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e?.target?.value)}
              placeholder="Collection name"
              className="mb-4"
            />
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameCollection}
                disabled={!newCollectionName?.trim()}
              >
                Rename
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-destructive mb-4">
              {selectedItems?.length > 0 
                ? `Delete ${selectedItems?.length} Item${selectedItems?.length > 1 ? 's' : ''}?`
                : 'Delete Collection?'
              }
            </h3>
            <p className="text-muted-foreground mb-6">
              {selectedItems?.length > 0 
                ? 'This action cannot be undone. The selected items will be permanently deleted.'
                : 'This action cannot be undone. All items in this collection will also be deleted.'
              }
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedItems([]);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={selectedItems?.length > 0 ? handleDeleteItems : handleDeleteCollection}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        collection={collection}
        onSave={handleSaveSharing}
      />
    </div>
  );
};

export default CollectionDetail;